import { ChangeEvent, Ref, useEffect, useRef, useState } from 'react';
import { DefaultDialog } from '../../../DefaultDialog';
import styles from '../../CreateMemorialQuiltStepThree/styles.module.scss';
import { EditorBlockHeader } from './components/EditorBlockHeader';
import { EditorBlockMiddle } from './components/EditorBlockMiddle';
import { EditorBlockBottom } from './components/EditorBlockBottom';
import { accessImageDialog } from '../../../../utilities/dialogs';
import { aspectImageByType, TypeImageEnum } from '../../../CreateMemorialQuiltComponents/CreateMemorialQuiltStepThree/const';
import { SelectChangeEvent } from '@mui/material';
import { ImageTypeModel } from '../../../CreateMemorialQuiltComponents/CreateMemorialQuiltStepThree/model';
import 'cropperjs/dist/cropper.css';
import './mobile-cropper.scss';
import Cropper from 'cropperjs';
import { useTranslates } from 'providers/translates';

type Props = {
  isLocal?: boolean;
  isAddImagesOnly?: boolean;
  ref: Ref<HTMLDivElement>;
  showClearImagesDialog: boolean;
  setShowClearImagesDialog: React.Dispatch<React.SetStateAction<boolean>>;
  resetImages: () => void;
  changeImages: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  filesImages: File[];
  deleteFileImage: (fileIndex: number) => Promise<void>;
  currentFileImage: File | null | undefined;
  isStepOne: boolean | undefined;
  currentCrop: Cropper | null;
  originalImage: string | null;
  setImageBlock: (image: ImageTypeModel | null) => void;
  isMain: boolean;
  setCurrentCrop: React.Dispatch<React.SetStateAction<Cropper | null>>;
  setCurrentFileImage: React.Dispatch<React.SetStateAction<File | null>>;
  setOriginalImage: React.Dispatch<React.SetStateAction<string | null>>;
};

const ImageEditorBlock: React.FC<Props> = ({
  isLocal,
  isAddImagesOnly,
  ref,
  showClearImagesDialog,
  setShowClearImagesDialog,
  resetImages,
  changeImages,
  filesImages,
  deleteFileImage,
  currentFileImage,
  isStepOne,
  currentCrop,
  originalImage,
  setImageBlock,
  isMain,
  setCurrentCrop,
  setCurrentFileImage,
  setOriginalImage
}) => {
  const { translates: t } = useTranslates();
  const addAPhoto = t?.quiltBuilderStepOne?.addAPhoto;
  const [aspectRatio, setAspectRatio] = useState<TypeImageEnum>(
    isStepOne ? TypeImageEnum.SQUARE : TypeImageEnum.PORTRAIT
  );
  const [confirmChooseImageDialog, setConfirmChooseImageDialog] = useState<boolean>(false);
  const [isNotAskAccessPermission, setIsNotAskAccessPermission] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // need for opening a window for pictures
  const openImageChoosing = () => {
    setConfirmChooseImageDialog(false);
    setIsNotAskAccessPermission(true);
    fileInputRef?.current && fileInputRef?.current?.click();
  };

  const changeCurrentFileImageType = (event: SelectChangeEvent<TypeImageEnum>) => {
    setAspectRatio(event.target.value as TypeImageEnum);
  };

  // image actions
  const changeImage = () => {
    setCurrentFileImage(null);
    setCurrentCrop(null);
    setOriginalImage(null);

    currentCrop?.destroy();
  };

  const cancelImage = () => {
    changeImage();
    setImageBlock(null);
  };

  const handleSetImage = (file: File) => {
    if (file) {
      setCurrentFileImage(file);
      setOriginalImage(file.name);
    }
  };

  useEffect(() => {
    if (!currentCrop) return;

    currentCrop.setAspectRatio(aspectImageByType[aspectRatio]);
  }, [aspectRatio]);

  useEffect(() => {
    if (!imageRef.current) return;

    if (currentCrop && currentFileImage) {
      currentCrop.replace(URL.createObjectURL(currentFileImage));
    } else {
      setCurrentCrop(
        new Cropper(imageRef.current, {
          aspectRatio: aspectImageByType[aspectRatio],
        })
      );
    }
  }, [imageRef.current, currentFileImage]);

  return (
    <>
      <DefaultDialog
        open={showClearImagesDialog}
        setDialog={setShowClearImagesDialog}
        title={addAPhoto?.deleteAlbumConfirmationTitle || 'Clear images'}
        text={addAPhoto?.deleteAlbumConfirmationContent || 'Are you sure you want to clear the album? All photos will be deleted and this cannot be undone.'}
        confirmAction={resetImages}
      />

      <div
        ref={ref}
        className={styles.editorBlock1}
        style={{
          ...(isAddImagesOnly && {
            height: '750px',
          }),
        }}
      >
        <EditorBlockHeader
          fileInputRef={fileInputRef}
          changeImages={changeImages}
          isNotAskAccessPermission={isNotAskAccessPermission}
          openImageChoosing={openImageChoosing}
          onSetConfirmChooseImageDialog={() => setConfirmChooseImageDialog(true)}
          isAddImagesOnly={isAddImagesOnly}
          cancelImage={cancelImage}
        />
        <span className={styles.isMobile}>{addAPhoto?.addImagesButtonInfo ?? 'Add your images to the album'}</span>
        <EditorBlockMiddle
          isAddImagesOnly={isAddImagesOnly}
          filesImages={filesImages}
          handleSetImage={handleSetImage}
          deleteFileImage={deleteFileImage}
          onSetShowClearImagesDialog={() => setShowClearImagesDialog(true)}
          currentFileImage={currentFileImage}
          imageRef={imageRef}
          isMain={isMain}
        />
        <EditorBlockBottom
          isAddImagesOnly={isAddImagesOnly}
          onSetShowClearImagesDialog={() => setShowClearImagesDialog(true)}
          isStepOne={isStepOne}
          currentCrop={currentCrop}
          aspectRatio={aspectRatio}
          changeCurrentFileImageType={changeCurrentFileImageType}
          changeImage={changeImage}
          originalImage={originalImage}
          onSetImageBlock={setImageBlock}
          cancelImage={cancelImage}
          currentFileImage={currentFileImage}
        />

        <DefaultDialog
          open={confirmChooseImageDialog}
          setDialog={setConfirmChooseImageDialog}
          title={addAPhoto?.addAPhotoAlbumAccessTitle || 'Access your photos'}
          text={addAPhoto?.addAPhotoAlbumAccessContent || 'We need your permission to access your photos.'}
          confirmAction={openImageChoosing}
        ></DefaultDialog>
      </div>
    </>
  );
}

export { ImageEditorBlock };
